import React, { useState, useEffect } from 'react';
import { Box, Typography, IconButton, Button } from '@mui/material';
import { Close as CloseIcon, WarningAmber as WarningIcon } from '@mui/icons-material';
import OpenPanesDropdown from './OpenPanesDropdown';

const PaneHeader = ({
  headerColor,
  icon,
  title,
  onClose,
  isDirty = false,
  // Controlled warning (e.g. triggered by ESC key or same-type click from parent)
  showCloseWarning = false,
  onCloseWarningDismiss,
  // Allow parent to override the discard action (e.g. "Discard & Switch" instead of "Discard & Close")
  onDiscard,
  discardLabel = 'Discard & Close',
  // Open panes dropdown
  openPanes = [],
  activePaneType,
  onSwitchPane,
}) => {
  const [internalWarning, setInternalWarning] = useState(false);

  const showWarning = internalWarning || showCloseWarning;

  // Clear internal warning when the form becomes clean (e.g. after save)
  useEffect(() => {
    if (!isDirty) setInternalWarning(false);
  }, [isDirty]);

  // Sync: if parent clears the controlled warning, clear internal too
  useEffect(() => {
    if (!showCloseWarning) setInternalWarning(false);
  }, [showCloseWarning]);

  const handleCloseClick = () => {
    if (isDirty) {
      setInternalWarning(true);
    } else {
      onClose();
    }
  };

  const handleDiscard = () => {
    setInternalWarning(false);
    onCloseWarningDismiss?.();
    if (onDiscard) {
      onDiscard();
    } else {
      onClose();
    }
  };

  const handleKeepEditing = () => {
    setInternalWarning(false);
    onCloseWarningDismiss?.();
  };

  if (showWarning) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '10px 20px',
          backgroundColor: headerColor,
          color: 'white',
          gap: 1,
          minHeight: 56,
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningIcon sx={{ fontSize: 18 }} />
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            Unsaved changes
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            size="small"
            onClick={handleDiscard}
            sx={{
              color: 'white',
              border: '1px solid rgba(255,255,255,0.5)',
              px: 1.5,
              py: 0.5,
              fontSize: '0.72rem',
              textTransform: 'none',
              '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)', borderColor: 'white' },
            }}
          >
            {discardLabel}
          </Button>
          <Button
            size="small"
            onClick={handleKeepEditing}
            sx={{
              color: 'white',
              backgroundColor: 'rgba(255,255,255,0.2)',
              px: 1.5,
              py: 0.5,
              fontSize: '0.72rem',
              textTransform: 'none',
              '&:hover': { backgroundColor: 'rgba(255,255,255,0.32)' },
            }}
          >
            Keep Editing
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '16px 20px',
        backgroundColor: headerColor,
        color: 'white',
        flexShrink: 0,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, flex: 1 }}>
        {icon}
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            textOverflow: 'ellipsis',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
          }}
        >
          {title}
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
        <OpenPanesDropdown
          openPanes={openPanes}
          activePaneType={activePaneType}
          onSwitch={onSwitchPane}
        />
        <IconButton
          onClick={handleCloseClick}
          sx={{ color: 'white', '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' } }}
        >
          <CloseIcon />
        </IconButton>
      </Box>
    </Box>
  );
};

export default PaneHeader;
